/**
 * The option codebooks for the förhandlingsframställan family.
 *
 * Codes are the legacy template's frozen ADR-0062 codes (what beta contracts
 * store); `value` is what the legacy template stored. Slot options carry
 * both, and the blocks accept either.
 */
/** § "Förhandlingen avser" — what the negotiation concerns. */
export const SCOPE_OPTIONS = [
    {
        code: 'general_development', value: 'allman_utveckling',
        label: 'Ändring av hyran till följd av den ekonomiska utvecklingen på orten (årlig hyreshöjning)',
        summary: 'Negotiation concerns the annual rent change reflecting general local economic development.',
    },
    { code: 'rent_change', value: 'andring_hyror', label: 'Ändring av hyror', summary: 'Negotiation concerns a change of rents.' },
    { code: 'other_terms', value: 'andra_villkor', label: 'Ändring av andra hyresvillkor', summary: 'Negotiation concerns a change of other rental terms.' },
    {
        code: 'first_rent_renovation', value: 'forsta_hyra_ombyggnad',
        label: 'Första hyra efter ombyggnad eller standardhöjande åtgärder',
        summary: 'Negotiation concerns the first rent after renovation or standard-raising measures.',
    },
    { code: 'new_construction', value: 'nybyggnation', label: 'Nybyggnation', summary: 'Negotiation concerns rent for new construction.' },
    { code: 'annat', value: 'annat', label: 'Annat', summary: 'Negotiation concerns some other matter specified as free text.' },
];
/**
 * Every scope except the ordinary yearly adjustment. Selecting one means
 * the yrkande goes beyond the annual change, so a grund must be stated.
 */
export const SCOPES_REQUIRING_GROUNDS = SCOPE_OPTIONS.filter((o) => o.code !== 'general_development').map((o) => o.code);
/** § "Yrkande" — the "andra hyresvillkor" alternatives. */
export const OTHER_TERMS_OPTIONS = [
    {
        code: 'individual_metering', value: 'individuell_matning',
        label: 'Individuell mätning och debitering av varm-/kallvatten eller el',
        summary: 'Term: individual metering and billing of hot/cold water or electricity.',
    },
    { code: 'change_surcharges', value: 'andring_tillagg', label: 'Ändring av tillägg', summary: 'Term: change of surcharges.' },
    { code: 'surcharge_per_apartment', value: 'tillagg_per_lgh', label: 'Tillägg på hyran per lägenhet', summary: 'Term: a rent surcharge per apartment.' },
    {
        code: 'common_areas', value: 'gemensamhetslokaler',
        label: 'Ändrade villkor för nyttjande av gemensamhetslokaler eller andra utrymmen',
        summary: 'Term: changed conditions for use of common areas or other spaces.',
    },
    { code: 'annat', value: 'annat', label: 'Annat', summary: 'Term: some other condition specified as free text.' },
];
/** § "Grund för yrkandet". */
export const GROUNDS_OPTIONS = [
    {
        code: 'investments', value: 'investeringar',
        label: 'Genomförda investeringar',
        summary: 'Grounds: investments that have been carried out.',
        detailKey: 'grounds_investeringar',
    },
    {
        code: 'changed_utility_value', value: 'bruksvarde',
        label: 'Förändrat bruksvärde',
        summary: 'Grounds: a changed bruksvärde (use value) of the dwelling.',
        detailKey: 'grounds_bruksvarde',
    },
    {
        code: 'comparison', value: 'jamforelse',
        label: 'Jämförelse med likvärdiga lägenheter i bruksvärdeshänseende (visar att hyresnivån för lägenheten är för låg)',
        renderLabel: 'Jämförelse med likvärdiga lägenheter i bruksvärdeshänseende',
        summary: 'Grounds: comparison with equivalent dwellings showing the rent level is too low.',
        detailKey: 'grounds_jamforelse',
    },
    { code: 'annat', value: 'annat', label: 'Annat', summary: 'Grounds: some other ground specified as free text.', detailKey: 'grounds_annat' },
];
/**
 * § "Underlag" — the enclosures. Each maps to an attachment the document
 * declares, so ticking one both prints the line and reserves its
 * "Bilaga N" slot.
 */
export const ENCLOSURE_OPTIONS = [
    { code: 'property_description', value: 'fastighetsbeskrivning', label: 'Fastighetsbeskrivning', summary: 'Enclosed: a property description.' },
    { code: 'apartment_list', value: 'lagenhetsforteckning', label: 'Lägenhetsförteckning', summary: 'Enclosed: an apartment register.' },
    { code: 'rent_roll', value: 'hyreslista', label: 'Hyreslista', summary: 'Enclosed: a rent roll.' },
    { code: 'financial_basis', value: 'ekonomiskt_underlag', label: 'Ekonomiskt underlag', summary: 'Enclosed: supporting financial documentation.' },
    { code: 'renovation_description', value: 'ombyggnadsbeskrivning', label: 'Ombyggnadsbeskrivning', summary: 'Enclosed: a renovation description.' },
];
export const SCOPE_EXTENT_OPTIONS = [
    { code: 'whole_property', value: 'hela', label: 'Hela fastigheten', summary: 'The request covers the entire property.' },
    {
        code: 'specific_apartments',
        value: 'lagenheter',
        label: 'Följande lägenheter',
        summary: 'The request covers only the listed apartments.',
    },
];
/** The document prints the rent type in lower case ("Hyran ska vara presumtionshyra."). */
export const NYBYGGNATION_RENT_TYPE_OPTIONS = [
    {
        code: 'presumption_rent',
        value: 'presumtionshyra',
        label: 'Presumtionshyra',
        renderLabel: 'presumtionshyra',
        summary: 'Rent for new construction is set as presumtionshyra (presumption rent).',
    },
    {
        code: 'utility_value_rent',
        value: 'bruksvardeshyra',
        label: 'Bruksvärdeshyra',
        renderLabel: 'bruksvärdeshyra',
        summary: 'Rent for new construction is set as bruksvärdeshyra (use-value rent).',
    },
];
//# sourceMappingURL=options.js.map